import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { TranslocoPipe } from '@jsverse/transloco';
import { UiButtonComponent } from '@vasta/frontend-ui-buttons';
import {
  UiMenuComponent,
  UiMenuItemComponent,
  UiMenuSectionComponent,
  UiMenuSeparatorComponent,
} from '@vasta/frontend-ui-overlay';
import { AuthFacade } from '@vasta/client-core-authentication';
import { ApiMeDtoTenantsInner } from '@vasta/api-vasta';

@Component({
  selector: 'cl-topbar-user',
  imports: [
    TranslocoPipe,
    UiButtonComponent,
    UiMenuComponent,
    UiMenuItemComponent,
    UiMenuSectionComponent,
    UiMenuSeparatorComponent,
  ],
  standalone: true,
  templateUrl: './topbar-user.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopbarUserComponent {
  readonly auth = inject(AuthFacade);
  private readonly router = inject(Router);

  readonly tenants = computed<ApiMeDtoTenantsInner[]>(
    () => this.auth.currentUser()?.tenants ?? [],
  );

  readonly currentTenantId = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      startWith(null),
      map(() => {
        let route = this.router.routerState.snapshot.root;
        while (route) {
          if (route.params['tenantId']) return route.params['tenantId'];
          if (!route.firstChild) break;
          route = route.firstChild;
        }
        return '';
      }),
    ),
    { initialValue: '' },
  );

  private readonly userMenu = viewChild<UiMenuComponent>('userMenu');

  openMenu(event: MouseEvent): void {
    this.userMenu()?.show(event.currentTarget as HTMLElement);
  }

  openProfile(): void {
    void this.router.navigate(['/', this.currentTenantId(), 'profile']);
  }

  openNewWorkspace(): void {
    void this.router.navigate(['/tenants/new']);
  }

  switchTenant(externalId: string): void {
    const segments = this.router.url.split('/').filter(Boolean);
    segments[0] = externalId;
    void this.router.navigate(['/', ...segments]);
  }

  signOut(): void {
    this.auth.logout();
  }
}
